import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './CheckinQuestions.css';

const TYPES = [
  { id: 'scale', label: '1–5 scale' },
  { id: 'faces', label: '😀 Faces (1–5)' },
  { id: 'bodymap', label: '🧍 Body map' },
  { id: 'yesno', label: 'Yes / No' },
  { id: 'text', label: 'Short text' },
];

// Inline "assign to classes" checklist. No boxes checked = the question applies
// to ALL classes (team-wide).
function ClassChecklist({ classes, isChecked, onToggle }) {
  return (
    <div style={{ padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, margin: '4px 0 8px' }}>
      <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
        Assign to specific classes — <strong>none checked = all classes</strong>.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {classes.length === 0 && <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No active classes found.</span>}
        {classes.map(c => (
          <label key={c.id} style={{ fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={isChecked(c.id)} onChange={() => onToggle(c.id)} />
            {c.class_name || c.name || `Class ${c.id}`}
          </label>
        ))}
      </div>
    </div>
  );
}

// Per-question "alert the coach when…" config. Contextual by question type.
function AlertConfig({ qtype, cmp, val, onChange }) {
  const panel = { padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, margin: '4px 0 8px' };
  const row = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: '0.85rem' };
  if (qtype === 'text') {
    return <div style={panel}><span style={{ color: '#991b1b' }}>Alerts aren't available for short-text answers.</span></div>;
  }
  return (
    <div style={panel}>
      <div style={{ fontSize: '0.78rem', color: '#991b1b', marginBottom: 6 }}>
        Flag the athlete on the coach's Today view when the answer is:
      </div>
      <div style={row}>
        <label style={{ cursor: 'pointer' }}><input type="radio" checked={!cmp} onChange={() => onChange(null, null)} /> Never</label>
        {(qtype === 'scale' || qtype === 'faces') && (
          <>
            <label style={{ cursor: 'pointer' }}><input type="radio" checked={cmp === 'lte'} onChange={() => onChange('lte', val || 2)} /> at or below</label>
            <label style={{ cursor: 'pointer' }}><input type="radio" checked={cmp === 'gte'} onChange={() => onChange('gte', val || 4)} /> at or above</label>
            {(cmp === 'lte' || cmp === 'gte') && (
              <select value={val || ''} onChange={e => onChange(cmp, Number(e.target.value))}>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
          </>
        )}
        {qtype === 'yesno' && (
          <>
            <label style={{ cursor: 'pointer' }}><input type="radio" checked={cmp === 'yes'} onChange={() => onChange('yes', null)} /> Yes</label>
            <label style={{ cursor: 'pointer' }}><input type="radio" checked={cmp === 'no'} onChange={() => onChange('no', null)} /> No</label>
          </>
        )}
        {qtype === 'bodymap' && (
          <label style={{ cursor: 'pointer' }}><input type="radio" checked={cmp === 'any'} onChange={() => onChange('any', null)} /> Any area tapped</label>
        )}
      </div>
    </div>
  );
}

// Admin editor for the quick wellness questions shown at practice check-in.
function CheckinQuestionsEditor({ onClose, onChanged }) {
  const toast = useToast();
  const [questions, setQuestions] = useState([]);
  const [draft, setDraft] = useState({ prompt: '', qtype: 'scale' });
  const [classes, setClasses] = useState([]);
  const [draftClassIds, setDraftClassIds] = useState([]);
  const [openPicker, setOpenPicker] = useState(null); // question id or 'draft'
  const [openAlert, setOpenAlert] = useState(null);   // question id or 'draft'
  const [draftAlert, setDraftAlert] = useState({ cmp: null, val: null });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${API_BASE_URL}/api/checkin/questions`);
      setQuestions(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`${API_BASE_URL}/api/classes?active_only=true`)
      .then(r => { const d = r.data; setClasses(Array.isArray(d) ? d : (d?.classes || [])); })
      .catch(() => {});
  }, []);

  // "Preview by class" — resolve exactly which questions a class asks at check-in
  // (team-wide + assigned), so a coach can VERIFY targeting (e.g. kindergym = none).
  const [previewClass, setPreviewClass] = useState('');
  const [previewQs, setPreviewQs] = useState([]);
  useEffect(() => {
    if (!previewClass) { setPreviewQs([]); return; }
    api.get(`${API_BASE_URL}/api/checkin/questions?classId=${previewClass}`)
      .then(r => setPreviewQs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPreviewQs([]));
  }, [previewClass, questions]);

  const inList = (ids, id) => (ids || []).map(Number).includes(Number(id));
  const toggleQuestionClass = (q, classId) => {
    const cur = (q.class_ids || []).map(Number);
    const next = inList(cur, classId) ? cur.filter(c => c !== Number(classId)) : [...cur, Number(classId)];
    updateQuestion(q.id, { class_ids: next });
  };
  const toggleDraftClass = (classId) => {
    setDraftClassIds(cur => inList(cur, classId) ? cur.filter(c => c !== Number(classId)) : [...cur, Number(classId)]);
  };
  const classBtnLabel = (ids) => (!ids || ids.length === 0) ? '🏷 All classes' : `🏷 ${ids.length} class${ids.length > 1 ? 'es' : ''}`;
  const alertBtnLabel = (cmp, val) => {
    if (cmp === 'lte') return `⚠️ ≤${val}`;
    if (cmp === 'gte') return `⚠️ ≥${val}`;
    if (cmp === 'yes') return '⚠️ Yes';
    if (cmp === 'no') return '⚠️ No';
    if (cmp === 'any') return '⚠️ any';
    return '⚠️ Alert';
  };
  const setQuestionAlert = (q, cmp, val) => updateQuestion(q.id, { alert_cmp: cmp, alert_val: val });

  const notifyChanged = () => onChanged && onChanged();

  const addQuestion = async () => {
    if (!draft.prompt.trim()) { toast?.error?.('Type a question first'); return; }
    try {
      await api.post(`${API_BASE_URL}/api/checkin/questions`, {
        prompt: draft.prompt.trim(), qtype: draft.qtype, sort_order: questions.length,
        class_ids: draftClassIds, alert_cmp: draftAlert.cmp, alert_val: draftAlert.val,
      });
      setDraft({ prompt: '', qtype: 'scale' });
      setDraftClassIds([]);
      setDraftAlert({ cmp: null, val: null });
      setOpenPicker(null); setOpenAlert(null);
      await load(); notifyChanged();
    } catch { toast?.error?.('Could not add question'); }
  };

  const updateQuestion = async (id, patch) => {
    try {
      await api.put(`${API_BASE_URL}/api/checkin/questions/${id}`, patch);
      setQuestions(qs => qs.map(q => (q.id === id ? { ...q, ...patch } : q)));
      notifyChanged();
    } catch { toast?.error?.('Could not save'); }
  };

  const removeQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`${API_BASE_URL}/api/checkin/questions/${id}`);
      setQuestions(qs => qs.filter(q => q.id !== id));
      notifyChanged();
    } catch { toast?.error?.('Could not delete'); }
  };

  return (
    <div className="ciq-overlay" onClick={onClose}>
      <div className="ciq-modal" onClick={e => e.stopPropagation()}>
        <div className="ciq-head">
          <h3>📝 Check-in questions</h3>
          <button className="ciq-close" onClick={onClose}>✕</button>
        </div>
        <p className="ciq-hint">Athletes answer these once per day at check-in (kiosk or from home). Questions marked 🎯 feed the 0–100 <strong>readiness</strong> score — this is now the single daily wellness survey. <strong>🏷 assign each question to specific classes</strong> so e.g. kindergym doesn't get them (no classes chosen = <em>every</em> class).</p>

        {/* Preview by class — verify exactly what a class asks */}
        <div style={{ padding: '8px 10px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, marginBottom: 10, fontSize: '0.83rem' }}>
          <label style={{ color: '#3730a3', fontWeight: 600 }}>👁 Preview a class:&nbsp;
            <select value={previewClass} onChange={e => setPreviewClass(e.target.value)} style={{ fontSize: '0.83rem', padding: '2px 6px', borderRadius: 6 }}>
              <option value="">choose…</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.class_name || c.name || `Class ${c.id}`}</option>)}
            </select>
          </label>
          {previewClass && (
            <div style={{ marginTop: 6, color: '#475569' }}>
              {previewQs.length === 0
                ? <span>This class asks <strong>nothing</strong> at check-in → athletes are marked present instantly.</span>
                : <span>Asks: {previewQs.map(q => q.prompt).join(' · ')}</span>}
            </div>
          )}
        </div>

        <div className="ciq-list">
          {questions.length === 0 && <p className="ciq-empty">No questions yet. Add up to a few quick ones below.</p>}
          {questions.map(q => (
            <div key={q.id}>
              <div className={`ciq-row ${q.active ? '' : 'off'}`}>
                <input className="ciq-prompt" defaultValue={q.prompt}
                  onBlur={e => { const v = e.target.value.trim(); if (v && v !== q.prompt) updateQuestion(q.id, { prompt: v }); }} />
                <select className="ciq-type" value={q.qtype} onChange={e => updateQuestion(q.id, { qtype: e.target.value })}>
                  {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <button
                  type="button"
                  style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: `1px solid ${(q.class_ids || []).length ? '#a5b4fc' : '#fcd34d'}`,
                    background: openPicker === q.id ? '#eff6ff' : (q.class_ids || []).length ? '#eef2ff' : '#fffbeb',
                    color: (q.class_ids || []).length ? '#3730a3' : '#92400e' }}
                  title={(q.class_ids || []).length ? 'Assigned to specific classes' : 'Applies to EVERY class — click to restrict'}
                  onClick={() => setOpenPicker(openPicker === q.id ? null : q.id)}
                >
                  {classBtnLabel(q.class_ids)}
                </button>
                <button
                  type="button"
                  style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: 6, border: '1px solid #fecaca', background: openAlert === q.id ? '#fef2f2' : '#fff', cursor: 'pointer', whiteSpace: 'nowrap', color: q.alert_cmp ? '#b91c1c' : '#94a3b8' }}
                  title="Alert the coach when this answer is concerning"
                  onClick={() => setOpenAlert(openAlert === q.id ? null : q.id)}
                >
                  {alertBtnLabel(q.alert_cmp, q.alert_val)}
                </button>
                {(q.qtype === 'scale' || q.qtype === 'faces') && (
                  <select className="ciq-type" title="Feeds the 0–100 readiness score"
                    value={q.readiness_role || ''}
                    onChange={e => updateQuestion(q.id, { readiness_role: e.target.value || null })}
                    style={{ maxWidth: 130, color: q.readiness_role ? '#4f46e5' : '#94a3b8' }}>
                    <option value="">🎯 not scored</option>
                    <option value="sleep_quality">🎯 Sleep</option>
                    <option value="soreness">🎯 Soreness</option>
                    <option value="energy">🎯 Energy</option>
                    <option value="mood">🎯 Mood</option>
                    <option value="stress">🎯 Stress</option>
                  </select>
                )}
                <label className="ciq-active" title="Show at check-in">
                  <input type="checkbox" checked={!!q.active} onChange={e => updateQuestion(q.id, { active: e.target.checked ? 1 : 0 })} /> on
                </label>
                <button className="ciq-del" title="Delete" onClick={() => removeQuestion(q.id)}>🗑</button>
              </div>
              {openPicker === q.id && (
                <ClassChecklist classes={classes} isChecked={(cid) => inList(q.class_ids, cid)} onToggle={(cid) => toggleQuestionClass(q, cid)} />
              )}
              {openAlert === q.id && (
                <AlertConfig qtype={q.qtype} cmp={q.alert_cmp} val={q.alert_val}
                  onChange={(cmp, val) => setQuestionAlert(q, cmp, val)} />
              )}
            </div>
          ))}
        </div>

        <div>
          <div className="ciq-add">
            <input className="ciq-prompt" placeholder="New question — e.g. How sore are you?"
              value={draft.prompt} onChange={e => setDraft({ ...draft, prompt: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') addQuestion(); }} />
            <select className="ciq-type" value={draft.qtype} onChange={e => setDraft({ ...draft, qtype: e.target.value })}>
              {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <button
              type="button"
              style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: openPicker === 'draft' ? '#eff6ff' : '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Assign the new question to specific classes"
              onClick={() => setOpenPicker(openPicker === 'draft' ? null : 'draft')}
            >
              {classBtnLabel(draftClassIds)}
            </button>
            <button
              type="button"
              style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: 6, border: '1px solid #fecaca', background: openAlert === 'draft' ? '#fef2f2' : '#fff', cursor: 'pointer', whiteSpace: 'nowrap', color: draftAlert.cmp ? '#b91c1c' : '#94a3b8' }}
              title="Alert the coach when this answer is concerning"
              onClick={() => setOpenAlert(openAlert === 'draft' ? null : 'draft')}
            >
              {alertBtnLabel(draftAlert.cmp, draftAlert.val)}
            </button>
            <button className="ciq-add-btn" onClick={addQuestion}>＋ Add</button>
          </div>
          {openPicker === 'draft' && (
            <ClassChecklist classes={classes} isChecked={(cid) => inList(draftClassIds, cid)} onToggle={toggleDraftClass} />
          )}
          {openAlert === 'draft' && (
            <AlertConfig qtype={draft.qtype} cmp={draftAlert.cmp} val={draftAlert.val}
              onChange={(cmp, val) => setDraftAlert({ cmp, val })} />
          )}
        </div>

        <div className="ciq-foot">
          <button className="ciq-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default CheckinQuestionsEditor;
